import http from "@/services/http";
import { CompanyInfo } from "@/types/company";
import { ICheckRecordingFinishedRes } from "@/types/share";
import {
  IAudioResInfo,
  IChapterApi,
  IDetailInfo,
  IGetAudioUrlParams,
  IGetMeetingDetailParams,
  IGetMeetingSummaryParams,
  IShareLinkInfo,
  ISpeakerSummaryApi,
  ISummary,
  ESummaryType,
  ISummaryEditContent,
  IDocComplete,
  ICreateShareParams,
  MeetingTitleData,
  IKeyframeItem,
  BackTraceData,
  IUpdateBackTraceParam,
  IBackTrace,
  MeetingPermissionData,
} from "@/types/summary";
import { AxiosRequestConfig } from "axios";


/**
 * 查询录屏是否完成
 * @param streamName
 * @returns
 */
export async function checkAuthRecordingFinished(streamName: string): Promise<ICheckRecordingFinishedRes> {
  const res = await http.get<ICheckRecordingFinishedRes>('/api/auth/record-server/record/splitResult', { params: { streamName } });
  return { recordStatus: res.data.recordStatus, finishStatus: res.data.finishStatus };
}

// 获取音频文件下载链接
export function getAudioUrl(params: IGetAudioUrlParams, config?: AxiosRequestConfig<unknown>) {
  return http
    .get<IAudioResInfo>(`/api/auth/record-server/meeting/record/download/url`, {
      params,
      timeout: 1000 * 60 * 5, // 5分钟超时
      ...config,
    })
    .then((res) => res.data);
}

// 获取录制详情
export async function getMeetingDetail(params: IGetMeetingDetailParams, config?: AxiosRequestConfig<unknown>) {
  console.log(">>> getMeetingDetail");
  const { recordingType, streamId } = params;
  const res = await http.get<IDetailInfo>(`/api/auth/record-server/meeting/detail/${recordingType}/${streamId}`, {
    timeout: 1000 * 60 * 5, // 5分钟超时
    ...config,
  });
  return res.data;
}

// 会议纪要总结
export function getMeetingSummary(params: IGetMeetingSummaryParams, config?: AxiosRequestConfig<unknown>) {
  return http
    .get<ISummary>("/api/auth/record-server/meeting/summary", {
      params,
      timeout: 1000 * 60 * 5, // 5分钟超时
      ...config,
    })
    .then((res) => res.data);
}

// 智能章节
export function getMeetingSmartChapters(params: IGetMeetingSummaryParams, config?: AxiosRequestConfig<unknown>) {
  return http
    .get<IChapterApi>("/api/auth/record-server/meeting/smart-chapters", {
      params,
      timeout: 1000 * 60 * 5, // 5分钟超时
      ...config
    })
    .then((res) => res.data);
}

// 发言人总结
export function getMeetingSpeakerSummary(params: IGetMeetingSummaryParams, config?: AxiosRequestConfig<unknown>) {
  return http
    .get<ISpeakerSummaryApi>("/api/auth/record-server/meeting/speaker-summary", {
      params,
      timeout: 1000 * 60 * 5, // 5分钟超时
      ...config
    })
    .then((res) => res.data);
}
// 获取分享链接
export function getShareLink(params: ICreateShareParams) {
  return http.post<IShareLinkInfo>("/api/auth/record-server/meeting/share/create", params);
}
// 标题重命名
export function renameRecord(streamName: string, title: string) {
  return http.post(`/api/auth/live-record/rename`, { streamName, title });
}

// 下载原文
export function getSrcDownloadUrl(params: { streamId: string; fileName: string, contentStep: string }) {
  const { streamId, fileName, contentStep } = params;
  return http.get(`/api/auth/record-server/meeting/download-src-doc?streamName=live/${streamId}&fileName=${fileName}_原文&contentStep=${contentStep}`);
}

// 下载纪要
export function getSummaryDownloadUrl(params: { streamId: string; fileName: string, contentStep: string }) {
  const { streamId, fileName, contentStep } = params;
  return http.get(`/api/auth/record-server/meeting/download-summary?streamName=live/${streamId}&fileName=${fileName}_智能总结&contentStep=${contentStep}`);
}

// 下载音频
export function getAudioDownloadUrl(params: { streamId: string; fileName: string }) {
  const { streamId, fileName } = params;
  return http.get(`/api/auth/record-server/meeting/audio/live/${streamId}?fileName=${fileName}_音频`);
}

// 编辑总结内容
export function editSummaryContent(params: { streamName: string; summaryType: ESummaryType; summaryInfo: ISummaryEditContent }) {
  return http.post(`/api/auth/record-server/summary/edit`, params);
}

// 编辑原文内容
export function editSrcContent(params: { streamName: string; completeDoc: IDocComplete }) {
  return http.post(`/api/auth/record-server/doc/edit`, params);
}

// 创建副本
export function copyRecord(streamName?: string) {
  return http.post(`/api/auth/record-server/backup`, { streamName });
}

// 获取会议标题
export function getMeetingTitle(streamName: string, reload = false, contentStep: string) {
  return http.get<MeetingTitleData>("/api/auth/record-server/meeting/title-summary", { params: { streamName, reload, contentStep }, timeout: 5 * 60 * 1000 });
}

// 获取关键帧
export function getKeyframes(streamName: string) {
  return http.get<IKeyframeItem[]>("/api/auth/record-server/keyframes", { params: { streamName }, timeout: 5 * 60 * 1000 });
}
// 观点回溯
export function getBackTrace(streamName: string, meetingSummaryEvent: string, reload: boolean, config?: AxiosRequestConfig<unknown>) {
  return http.get<BackTraceData>("/api/auth/record-server/meeting/backTrace", { params: { streamName, contentStep: "SRC_DOC", meetingSummaryEvent, reload }, timeout: 5 * 60 * 1000, ...config });
}

// 更新观点回溯
export function updateBackTrace(streamName: string, meetingSummaryEvent: string, backTrace: IBackTrace) {
  return http.post<IUpdateBackTraceParam>("/api/auth/record-server/meeting/backTrace/edit", { streamName, meetingSummaryEvent, backTrace });
}

// 获取会议访问权限
export function getMeetingPermission(streamName: string) {
  return http.get<MeetingPermissionData>("/api/auth/live-record/meeting/permission", { params: { streamName } });
}

// 查询有权限访问会议的企业
export function getMeetingViewableOrganization(streamName: string) {
  return http.get<CompanyInfo[]>("/api/auth/live-record/meeting/viewable/organization", { params: { streamName } });
}